import React from 'react'

import { components } from 'react-select'
import cx from 'classnames'

import { ADDRESS_TYPE } from 'definitions'
import { useDebounce } from 'hooks/useDebounce'
import { useStateCallback } from 'hooks/useStateCallback'
import Icon from 'common/Icon'
import PlaceholderOption from 'common/PlaceholderOption'
import RadioGroup from 'common/Forms/RadioGroup'
import ReactSelect from 'common/Forms/ReactSelect'
import ShowMoreButton from 'common/ShowMoreButton'

import CartSummaryDeliveryAddressPopup from './CartSummaryDeliveryAddressPopup'
import CartSummaryDeliveryOtherAddress from './CartSummaryDeliveryOtherAddress'

export default ({ resource, webshopCartStore, clientId, cart, disabled, updateCart }) => {
  const [addressType, setAddressType] = useStateCallback(cart.deliveryInfo?.addressType || ADDRESS_TYPE.CLIENT)
  const [inputValue, setInputValue] = React.useState('')
  const [isOpen, setIsOpen] = React.useState()
  const [options, setOptions] = React.useState([])
  const [page, setPage] = React.useState(1)
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [menuIsOpen, setMenuIsOpen] = React.useState(undefined)
  const [hasSelected, setHasSelected] = React.useState(false)
  const pageSize = 50
  // const [otherAddress, setOtherAddress] = React.useState(cart.deliveryInfo?.address)

  const isClient = addressType === ADDRESS_TYPE.CLIENT
  const isOther = addressType === ADDRESS_TYPE.OTHER
  const otherAddressCheckBoxId = `other-address-checkbox-id-${cart.id}`

  const handleChangeAddressType = selected => setAddressType(selected, () => updateCart())

  const toggle = () => setIsOpen(prev => !prev)

  const loadOptions = React.useCallback(
    async (searchTerm, pageNum) => {
      setLoadingMore(true)
      const res = await webshopCartStore.getClientSites({
        clientId,
        search: searchTerm,
        size: pageSize,
        page: pageNum,
      })
      setLoadingMore(false)

      if (pageNum === 1) {
        setOptions(res.data)
      } else {
        setOptions(prev => [...prev, ...res.data])
      }

      const input = document.querySelector('#clientSiteEleId .form-control input')
      if (input) {
        input.focus()
      }
    },
    [webshopCartStore, pageSize]
  )

  React.useEffect(() => {
    loadOptions(inputValue, page)
  }, [inputValue, page, loadOptions])

  const debouncedSetInputValue = useDebounce(value => {
    setInputValue(value)
    setPage(1)
    setHasSelected(false)
  }, 300)

  const handleInputChange = (newValue, actionMeta) => {
    // Only handle user input, not other events
    if (actionMeta.action === 'input-change') {
      debouncedSetInputValue(newValue)
      return newValue
    }
    return newValue
  }

  const handleChange = () => {
    // Clear the input value after selecting an option
    setInputValue('')
    setPage(1)
    setHasSelected(true)
    updateCart()
  }

  const handleMenuOpen = () => {
    setMenuIsOpen(true)
  }

  const handleMenuClose = () => {
    setMenuIsOpen(false)
  }

  const handleBlur = () => {
    // When input loses focus and no selection was made, load default list
    const hasSearchTerm = inputValue && inputValue.trim().length > 0

    if (hasSearchTerm && !hasSelected) {
      // Load default list without search term
      setInputValue('')
      setPage(1)
      setHasSelected(false)
    }
  }

  const optionsWithLoadMore = React.useMemo(() => {
    const result = Array.isArray(options) ? [...options] : []
    const totalLoaded = result.length
    const hasMore = totalLoaded > 0 && totalLoaded === pageSize * page

    if (hasMore) {
      result.push({ isLoadMore: true, label: resource.ShowMore })
    }
    return result
  }, [options, page, pageSize, resource])

  return (
    <React.Fragment>
      <RadioGroup
        radios={[
          {
            value: ADDRESS_TYPE.CLIENT,
            label: resource.ClientSites,
            children: isClient && (
              <ReactSelect
                id="clientSiteEleId"
                isDisabled={disabled}
                isSearchable={true}
                name="deliveryInfo.clientAddress"
                rules={{ required: isClient && resource.Required }}
                formGroup
                optionValueKey="clientSiteId"
                optionLabelKey="name"
                options={optionsWithLoadMore}
                onInputChange={handleInputChange}
                onChange={handleChange}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onBlur={handleBlur}
                menuIsOpen={menuIsOpen}
                isLoading={loadingMore}
                filterOption={null}
                components={{
                  Option: ({ data, innerRef, ...innerProps }) => {
                    if (data.isPlaceholder) {
                      return <PlaceholderOption ref={innerRef} label={data.label} />
                    }

                    if (data.isLoadMore) {
                      return (
                        <ShowMoreButton
                          resource={resource}
                          disabled={loadingMore}
                          innerProps={innerProps}
                          setPage={setPage}
                        />
                      )
                    }

                    const address = data.address || {}
                    return (
                      <components.Option data={data} {...innerProps}>
                        <strong className="d-block">{data.name}</strong>
                        <small className="d-block muted address-mini">{`${address.street || ''} ${address.number || ''} ${address.box || ''}, ${address.postalCode || ''} ${address.city || ''}, ${address.country?.name || ''}`}</small>
                      </components.Option>
                    )
                  },
                }}
              />
            ),
          },
          {
            id: otherAddressCheckBoxId,
            value: ADDRESS_TYPE.OTHER,
            label: (
              <React.Fragment>
                <span>{resource.OtherAddress}</span>
                {isOther && (
                  <Icon
                    icon="edit_note"
                    className="text-primary position-absolute"
                    style={{ top: 0, right: '-30px', bottom: 0 }}
                    title={resource.EditOtherAddress}
                  />
                )}
              </React.Fragment>
            ),
            labelClassName: 'position-relative',
          },
        ]}
        name="deliveryInfo.addressType"
        rules={{ required: resource.Required }}
        label={<h5 className="webshop-title-color fw-bolder mb-0">{resource.DeliveryAddress}</h5>}
        formGroup
        formClassName={cx(isOther && 'mb-0')}
        disabled={disabled}
        onChange={handleChangeAddressType}
      />
      <CartSummaryDeliveryAddressPopup
        isOpen={isOpen}
        target={otherAddressCheckBoxId}
        isOther={isOther}
        resource={resource}
        disabled={disabled}
        address={cart.deliveryInfo?.address}
        toggle={toggle}
        updateCart={() => updateCart()}
      />
      {isOther && <CartSummaryDeliveryOtherAddress resource={resource} />}
    </React.Fragment>
  )
}
